import React, { useState, useEffect } from "react";
import { Table, Button, Form, message, Modal } from "antd";
import API from "api/endpoints";
import moment from "moment";
import { HardwareAssetsService } from "Services/AdminServices/AssetsServices/HardwareService";
import axiosInstance from "api/axiosInstance";

const useHardwareAssets = () => {
  const [hardwareAssets, setHardwareAssets] = useState([]);

  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  const fetchDepartments = async () => {
    try {
      const res = await axiosInstance.get(API.department.getList);
      setDepartments(res.data);
    } catch (error) {
      message.error("Failed to load departments");
    }
  };

  useEffect(() => {
    fetchHardwareAssets();
    fetchDepartments();
  }, []);

  const fetchEmployeesByDepartment = async (departmentId) => {
    try {
      const res = await axiosInstance.get(API.employee.getAllOverview(departmentId));
      setEmployees(res.data);
    } catch (error) {
      message.error("Failed to fetch employees");
    }
  };

  const fetchHardwareAssets = async () => {
    try {
      const response = await HardwareAssetsService.fetchHardwareAssets();
      const assets = response;
      setHardwareAssets(assets);
    } catch (error) {
      console.error("Error fetching Hardware assets:", error);
      message.error("Failed to fetch Hardware assets.");
    }
  };

  const toggleAddForm = () => {
    setIsAdding(!isAdding);
    form.resetFields();
  };

  const handleAddHardwareAsset = async (values) => {
    try {
      setIsLoading(true);

      const payload = {
        employeeId: values.employeeId,
        name: values.name,
        category: values.category,
        assetsCost: Number(values.assetsCost),
        serial: values.serial,
        dateLoaned: values.dateLoaned.format("YYYY-MM-DD"),
        dateReturned: values.dateReturned.format("YYYY-MM-DD"),
        notes: values.notes,
        description: values.description,
      };
      await axiosInstance.post(API.hardwareAssets.add, payload);
      message.success("Hardware Asset added successfully");
      toggleAddForm();
      fetchHardwareAssets();
    } catch (error) {
      console.error("Add Hardware Asset Error:", error.response?.data || error);
      message.error("Failed to add Hardware asset");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { title: "Employee Id", dataIndex: "employeePublicId", key: "employeePublicId", render: (v) => v || "-" },
    {
      title: "Employee",
      key: "employee",
      render: (_, record) => (
        <div>
          <div>{record.employeeName || "-"}</div>
          {record.employeeWorkEmail && (
            <div style={{ fontSize: 12, color: "#888" }}>{record.employeeWorkEmail}</div>
          )}
        </div>
      ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Department", dataIndex: "category", key: "category" },
    {
      title: "Assets Cost",
      dataIndex: "assetsCost",
      key: "assetsCost",
      render: (cost) => `$${cost.toFixed(2)}`,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewMore(record)}>
          View More
        </Button>
      ),
    },
  ];

  const additionalColumns = [
    { title: "Date Loaned", dataIndex: "dateLoaned", key: "dateLoaned" },
    {
      title: "Date Returned",
      dataIndex: "dateReturned",
      key: "dateReturned",
      render: (date) => moment(date).format("MMM-DD-yyyy"),
    },
    { title: "Notes", dataIndex: "notes", key: "notes" },
    { title: "Description", dataIndex: "description", key: "description" },
  ];

  const handleViewMore = (asset) => {
    Modal.info({
      title: "Additional Data",
      content: (
        <div>
          <Table
            dataSource={[asset]}
            columns={additionalColumns}
            className="custom-table"
            pagination={false}
            rowKey="licenseKey"
          />
        </div>
      ),
      centered: true,
      width: "800px",
    });
  };

  const disablePastDates = (current) => current && current < moment().startOf("day");
  const disableExpiryDate = (current) => {
    const subscriptionDate = form.getFieldValue("subscriptionDate");
    return (
      current && (current.isSame(subscriptionDate, "day") || current.isBefore(subscriptionDate))
    );
  };

  return {
    hardwareAssets,
    departments,
    employees,
    isAdding,
    isLoading,
    form,
    columns,
    toggleAddForm,
    handleAddHardwareAsset,
    fetchEmployeesByDepartment,
    disablePastDates,
    disableExpiryDate,
  };
};

export default useHardwareAssets;
